import React, { useCallback, useRef, useState } from 'react';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useLocalStorage, useMediaQuery } from 'usehooks-ts';
import { Attachment } from 'graphql-common';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ImageViewer from '@lib/components/ImageViewer/ImageViewer';
import Modal from '@lib/components/Modal/Modal';
import PDFViewer from '@lib/components/PDFViewer/PDFViewer';
import VideoViewer, {
  VideoViewerHandle,
} from '@lib/components/VideoViewer/VideoViewer';
import {
  VIDEO_PROCESSING_POLL_INTERVAL_MS,
  VIDEO_PROCESSING_POLL_TIMEOUT_MS,
} from '@lib/components/VideoViewer/constants';
import { ModalMode } from '@lib/components/Modal/enums';
import { FILE_PREVIEW_MODAL_MODE } from '@lib/enums/localStorageKeys';
import isMobile from '@lib/utils/isMobile';
import { BELOW_MD1_QUERY } from '@lib/assets/styles/breakpoints';
import videoStyles from '@lib/components/VideoViewer/VideoViewer.module.scss';
import useProcessingPolling from './useProcessingPolling';
import styles from './FileViewerModal.module.scss';

type Props = {
  attachment: Attachment | null | undefined;
  attachmentName?: string;
  isOpen: boolean;
  onClose: () => void;
  swiperProps?: {
    onPrevious?: () => void;
    onNext?: () => void;
    isPreviousDisabled?: boolean;
    isNextDisabled?: boolean;
  };
  useDownloadTool?: boolean;
  useFavoriteTool?: boolean;
  onFavoriteToggle?: () => void;
  isFavorited?: boolean;
  isFavoriteLoading?: boolean;
  useMarkAsReadTool?: boolean;
  /**
   * Called when the user explicitly marks the document as read/watched.
   * Receives the elapsed reading-duration in seconds:
   * - For video: real watched seconds from `<video>` playback. The explicit
   *   CTA is disabled until the user has accrued >0 watched seconds, so this
   *   value is always positive at call time.
   * - For PDF/image: modal-open elapsed time (`Date.now() - openedAt`),
   *   floored at 1 second.
   */
  onMarkAsRead?: (readingDurationSeconds: number) => void;
  isMarkAsReadLoading?: boolean;
  /**
   * Optional callback fired periodically while a video is transcoding so the
   * parent can refetch the attachment payload. When provided, polling runs at
   * `VIDEO_PROCESSING_POLL_INTERVAL_MS` and caps at
   * `VIDEO_PROCESSING_POLL_TIMEOUT_MS`. When omitted, the placeholder still
   * renders but no polling is triggered.
   */
  onRefetchAttachment?: () => void;
};

export default function FileViewerModal(props: Props) {
  const { t } = useTranslation();
  const [modalMode, setModalMode] = useLocalStorage(
    FILE_PREVIEW_MODAL_MODE,
    ModalMode.Default,
  );
  const {
    attachment,
    isOpen,
    onClose,
    attachmentName,
    swiperProps,
    useDownloadTool = true,
    useFavoriteTool = false,
    onFavoriteToggle,
    isFavorited,
    isFavoriteLoading,
    useMarkAsReadTool = false,
    onMarkAsRead,
    isMarkAsReadLoading = false,
    onRefetchAttachment,
  } = props;

  const downloadUrl = attachment?.urls?.originalDirectDownload || '';
  const fileUrl = attachment?.urls?.large || attachment?.urls?.original || '';
  const mimeType = _get(attachment, [
    'fileData',
    'data',
    'metadata',
    'mimeType',
  ]) as string | undefined;
  const isPdf = mimeType?.includes('pdf');
  const isImage = mimeType?.includes('image');
  const isVideo = mimeType?.startsWith('video');

  // On mobile devices and below the $md1 breakpoint the modal is already
  // edge-to-edge, so we force fullscreen and hide the toggle (passing it as
  // `undefined` below) to keep the two states coherent.
  const isBelowMd1 = useMediaQuery(BELOW_MD1_QUERY);
  const { phone, tablet } = isMobile();
  const forceFullScreen = isBelowMd1 || phone || tablet;

  const onFullScreenToggle = () =>
    setModalMode((prevState) =>
      prevState === ModalMode.Default
        ? ModalMode.FullScreen
        : ModalMode.Default,
    );
  const isFullScreen = forceFullScreen || modalMode === ModalMode.FullScreen;
  const mode = isFullScreen ? ModalMode.FullScreen : ModalMode.Default;

  // `openedAtRef` drives PDF/image reading-duration (modal-open elapsed).
  // Video uses its own playback-elapsed counter via `videoViewerRef`.
  const openedAtRef = useRef<number | null>(null);
  if (isOpen && openedAtRef.current === null) openedAtRef.current = Date.now();
  if (!isOpen && openedAtRef.current !== null) openedAtRef.current = null;

  const videoViewerRef = useRef<VideoViewerHandle>(null);
  // Gates the "Mark as watched" CTA — disabled until VideoViewer signals the
  // user has accrued any real watched time (via `onWatchStart`). Reset per
  // attachment-id below so re-opening a different doc starts fresh.
  const [hasStartedWatchingVideo, setHasStartedWatchingVideo] = useState(false);
  // Reset watch-started state whenever attachment changes.
  const attachmentIdRef = useRef<string | null | undefined>(undefined);
  if (attachmentIdRef.current !== attachment?.id) {
    attachmentIdRef.current = attachment?.id;
    if (hasStartedWatchingVideo) setHasStartedWatchingVideo(false);
  }

  // Video is "processing" when BE has not yet marked the attachment processed
  // and no transcoded variant has appeared yet.
  // `original` is included because the placeholder is about playability —
  // if BE populated `original` before transcoding finished, the user can
  // still play the raw upload.
  const hasPlayableUrl =
    !!attachment?.urls?.video1080p ||
    !!attachment?.urls?.video720p ||
    !!attachment?.urls?.video480p ||
    !!attachment?.urls?.original;
  const isProcessingVideo =
    !!isVideo && attachment?.processed === false && !hasPlayableUrl;

  const { timedOut: processingTimedOut } = useProcessingPolling({
    enabled: !!(isOpen && isProcessingVideo && onRefetchAttachment),
    onPoll: () => onRefetchAttachment?.(),
    intervalMs: VIDEO_PROCESSING_POLL_INTERVAL_MS,
    timeoutMs: VIDEO_PROCESSING_POLL_TIMEOUT_MS,
  });

  const computeReadingDurationSeconds = useCallback((): number => {
    if (isVideo) {
      return videoViewerRef.current?.getWatchedSeconds() ?? 0;
    }
    const openedAt = openedAtRef.current ?? Date.now();
    return Math.max(1, Math.floor((Date.now() - openedAt) / 1000));
  }, [isVideo]);

  const triggerMarkAsRead = useCallback(() => {
    if (!onMarkAsRead) return;
    const seconds = computeReadingDurationSeconds();
    if (seconds <= 0) return;
    onMarkAsRead(seconds);
  }, [onMarkAsRead, computeReadingDurationSeconds]);

  const handleWatchStart = useCallback(() => {
    setHasStartedWatchingVideo(true);
  }, []);

  const ctaLabel = isVideo
    ? t('i-have-watched-this-video')
    : t('i-have-read-this-document');
  const ctaTestId = isVideo
    ? 'file-viewer-modal__mark-as-watched'
    : 'file-viewer-modal__mark-as-read';

  const isCtaDisabled = isVideo && !hasStartedWatchingVideo;
  const fixedActions =
    useMarkAsReadTool && onMarkAsRead && !isProcessingVideo ? (
      <Button
        buttonText={ctaLabel}
        buttonType={ButtonTypes.primaryFilled}
        buttonSize={ButtonSizes.large}
        rightIcon="check"
        fullWidth
        isLoading={isMarkAsReadLoading}
        disabled={isCtaDisabled}
        onClick={triggerMarkAsRead}
        data-test-id={ctaTestId}
      />
    ) : undefined;

  if (!(isPdf || isImage || isVideo)) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      caption={attachmentName}
      modalContentClassName={styles.modalContentClassName}
      modalBodyClassName={styles.modalBodyClassName}
      mode={mode}
      fixedActions={fixedActions}
    >
      <>
        {isPdf && (
          <PDFViewer
            fileUrl={fileUrl}
            downloadUrl={downloadUrl}
            onFullScreenToggle={
              forceFullScreen ? undefined : onFullScreenToggle
            }
            isFullScreen={isFullScreen}
            useDownloadTool={useDownloadTool}
            useFavoriteTool={useFavoriteTool}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
        {isImage && (
          <ImageViewer
            fileUrl={fileUrl}
            downloadUrl={downloadUrl}
            onFullScreenToggle={
              forceFullScreen ? undefined : onFullScreenToggle
            }
            swiperProps={swiperProps}
            useDownloadTool={useDownloadTool}
            useFavoriteTool={useFavoriteTool}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
        {isVideo && isProcessingVideo && (
          <div
            className={videoStyles.processingPlaceholder}
            data-test-id="file-viewer-modal__video-processing"
          >
            <CircleLoader />
            <span className={videoStyles.processingPlaceholderCaption}>
              {processingTimedOut
                ? t('still-processing-try-again-later')
                : t('processing-video')}
            </span>
          </div>
        )}
        {isVideo && !isProcessingVideo && (
          <VideoViewer
            ref={videoViewerRef}
            fileUrl={fileUrl}
            downloadUrl={downloadUrl}
            posterUrl={attachment?.urls?.imagePreview || undefined}
            videoUrls={{
              video1080p: attachment?.urls?.video1080p,
              video720p: attachment?.urls?.video720p,
              video480p: attachment?.urls?.video480p,
              original: attachment?.urls?.original,
            }}
            onWatchStart={handleWatchStart}
            onFullScreenToggle={onFullScreenToggle}
            isFullScreen={isFullScreen}
            useDownloadTool={useDownloadTool}
            useFavoriteTool={useFavoriteTool}
            onFavoriteToggle={onFavoriteToggle}
            isFavorited={isFavorited}
            isFavoriteLoading={isFavoriteLoading}
          />
        )}
      </>
    </Modal>
  );
}
